import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Search, Plus, Building2, Phone, MapPin,
  Edit2, Trash2, MoreHorizontal, AlertTriangle
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import SkeletonCard from '@/components/ui/SkeletonCard';
import LeadForm from '@/components/leads/LeadForm';
import ContactDetailSheet from '@/components/contacts/ContactDetailSheet';
import { getKundentypBadgeClass } from '@/lib/kundentyp';
import { EIGENE_ZUSTELLUNG_KEY } from '@/lib/useEigeneZustellung';
import { getCategoryColor, getCategoryIcon, getScoreColor } from '@/components/utils/LeadCategorization';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notifyLeadAssignedToAgent } from '@/components/notifications/notificationHelper';
import { LEAD_STATUSES, normalizeLeadStatus } from '@/lib/leadStatus';

const STATUS_ORDER = LEAD_STATUSES;

export default function AdminLeads() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brancheFilter, setBrancheFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const queryClient = useQueryClient();

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['admin-leads'],
    queryFn: () => base44.entities.Lead.list('name'),
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const lead = await base44.entities.Lead.create(data);
      // Notify assigned agent
      if (data.assigned_agent) {
        notifyLeadAssignedToAgent(lead, data.assigned_agent);
      }
      return lead;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-leads']);
      queryClient.invalidateQueries({ queryKey: EIGENE_ZUSTELLUNG_KEY });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Lead.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-leads']);
      queryClient.invalidateQueries({ queryKey: EIGENE_ZUSTELLUNG_KEY });
      setEditingLead(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Lead.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-leads']);
      setDeleteConfirm(null);
    },
  });

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.ort?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || normalizeLeadStatus(lead.status) === statusFilter;
    const matchesBranche = brancheFilter === 'all' || lead.branche === brancheFilter;
    const matchesCategory = categoryFilter === 'all' || lead.priority_category === categoryFilter;
    const matchesAgent = agentFilter === 'all'
      || (agentFilter === 'none'
        ? !lead.assigned_agent?.trim()
        : (lead.assigned_agent || '').trim().toLowerCase() === agentFilter.toLowerCase());
    return matchesSearch && matchesStatus && matchesBranche && matchesCategory && matchesAgent;
  }).sort((a, b) => {
    const statusDiff = STATUS_ORDER.indexOf(normalizeLeadStatus(a.status)) - STATUS_ORDER.indexOf(normalizeLeadStatus(b.status));
    if (statusDiff !== 0) return statusDiff;
    return a.name.localeCompare(b.name);
  });

  const uniqueBranchen = [...new Set(leads.map(l => l.branche).filter(Boolean))];

  // Agentenliste: Groß-/Kleinschreibung-tolerant dedupliziert (wie AdminTasks)
  const uniqueAgents = (() => {
    const seen = new Map();
    leads.forEach(l => {
      const key = (l.assigned_agent || '').trim().toLowerCase();
      if (key && !seen.has(key)) seen.set(key, l.assigned_agent.trim());
    });
    return [...seen.values()].sort();
  })();

  const handleSubmit = (data) => {
    if (editingLead) {
      updateMutation.mutate({ id: editingLead.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <PageHeader title="Kontakte" subtitle={`${leads.length} gesamt`} />
            <Button onClick={() => { setEditingLead(null); setShowForm(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Neuer Kontakt</span>
              <span className="sm:hidden">Neu</span>
            </Button>
          </div>

          {/* Search */}
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Filters - Desktop inline, Mobile collapsible */}
          <div className="hidden md:flex flex-wrap gap-3 mt-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                {STATUS_ORDER.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={brancheFilter} onValueChange={setBrancheFilter}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Branche" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Branchen</SelectItem>
                {uniqueBranchen.map(b => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="Kategorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                <SelectItem value="Hohe Priorität">Hohe Priorität</SelectItem>
                <SelectItem value="Bestandskunden-Nähe">Bestandskunden-Nähe</SelectItem>
                <SelectItem value="Neue Branche">Neue Branche</SelectItem>
                <SelectItem value="Standard">Standard</SelectItem>
              </SelectContent>
            </Select>
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Agent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Agenten</SelectItem>
                <SelectItem value="none">Ohne Agent</SelectItem>
                {uniqueAgents.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mobile filter toggle */}
          <div className="md:hidden mt-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setShowFilters(!showFilters)}
            >
              Filter {showFilters ? 'ausblenden' : 'anzeigen'}
              {(statusFilter !== 'all' || brancheFilter !== 'all' || categoryFilter !== 'all' || agentFilter !== 'all') && (
                <Badge className="ml-2 bg-brand-900 text-white h-5 px-1.5">aktiv</Badge>
              )}
            </Button>
            {showFilters && (
              <div className="flex flex-col gap-2 mt-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    {STATUS_ORDER.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={brancheFilter} onValueChange={setBrancheFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Branche" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Branchen</SelectItem>
                    {uniqueBranchen.map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kategorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Kategorien</SelectItem>
                    <SelectItem value="Hohe Priorität">Hohe Priorität</SelectItem>
                    <SelectItem value="Bestandskunden-Nähe">Bestandskunden-Nähe</SelectItem>
                    <SelectItem value="Neue Branche">Neue Branche</SelectItem>
                    <SelectItem value="Standard">Standard</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={agentFilter} onValueChange={setAgentFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Agent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Agenten</SelectItem>
                    <SelectItem value="none">Ohne Agent</SelectItem>
                    {uniqueAgents.map(a => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {filteredLeads.map(lead => (
            <Card
              key={lead.id}
              className="p-4 cursor-pointer hover:shadow-md transition-shadow active:scale-[0.99]"
              onClick={() => setSelectedLead(lead)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                      {lead.name}
                      {!lead.email && (
                        <span title="E-Mail fehlt — bitte pflegen!">
                          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-slate-500">{lead.branche}</p>
                    {lead.ort && (
                      <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {lead.ort}
                      </p>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingLead(lead); setShowForm(true); }}>
                      <Edit2 className="w-4 h-4 mr-2" />
                      Bearbeiten
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(lead); }}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Löschen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <StatusBadge status={lead.status} type="lead" />
                <Badge variant="outline" className={getKundentypBadgeClass(lead.kundentyp)}>
                  {lead.kundentyp || 'B2B'}
                </Badge>
                {lead.shopify_versand === false && (
                  <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300" title="Bestellungen gehen nicht an Shopify (kein DPD-Etikett)">
                    Eigene Zustellung
                  </Badge>
                )}
                {lead.priority_category && (
                  <Badge className={getCategoryColor(lead.priority_category)} variant="outline">
                    {getCategoryIcon(lead.priority_category)} {lead.priority_category}
                  </Badge>
                )}
                {lead.lead_score !== undefined && (
                  <Badge className={getScoreColor(lead.lead_score)}>
                    {lead.lead_score}
                  </Badge>
                )}
              </div>
              {lead.telefon && (
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {lead.telefon}
                </p>
              )}
            </Card>
          ))}
          {isLoading && filteredLeads.length === 0 && (
            <SkeletonCard count={4} />
          )}
          {!isLoading && filteredLeads.length === 0 && (
            <EmptyState
              icon={Building2}
              title="Keine Kontakte"
              description="Es wurden keine Kontakte gefunden, die Ihren Filtern entsprechen."
              compact
            />
          )}
        </div>

        {/* Desktop Table View */}
        <Card className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Branche</TableHead>
                <TableHead>Ort</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Kategorie</TableHead>
                <TableHead>Ansprechpartner</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.map(lead => (
                <TableRow
                  key={lead.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => setSelectedLead(lead)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <div className="font-medium flex items-center gap-1.5">
                          {lead.name}
                          {!lead.email && (
                            <span title="E-Mail fehlt — bitte pflegen!">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                            </span>
                          )}
                          <Badge variant="outline" className={`${getKundentypBadgeClass(lead.kundentyp)} text-[9px] uppercase`}>
                            {lead.kundentyp || 'B2B'}
                          </Badge>
                          {lead.shopify_versand === false && (
                            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-[9px] uppercase" title="Bestellungen gehen nicht an Shopify (kein DPD-Etikett)">
                              Eigene Zustellung
                            </Badge>
                          )}
                        </div>
                        {lead.telefon && (
                          <p className="text-sm text-slate-500">{lead.telefon}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{lead.branche}</TableCell>
                  <TableCell>{lead.ort}</TableCell>
                  <TableCell>
                    <StatusBadge status={lead.status} type="lead" />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {lead.priority_category && (
                        <Badge className={getCategoryColor(lead.priority_category)} variant="outline">
                          {getCategoryIcon(lead.priority_category)} {lead.priority_category}
                        </Badge>
                      )}
                      {lead.lead_score !== undefined && (
                        <Badge className={getScoreColor(lead.lead_score)}>
                          {lead.lead_score}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{lead.assigned_agent}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingLead(lead); setShowForm(true); }}>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirm(lead); }}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </main>

      {/* Lead Form Sheet */}
      <Sheet open={showForm} onOpenChange={setShowForm}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>{editingLead ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}</SheetTitle>
          </SheetHeader>
          <LeadForm
            lead={editingLead}
            onSubmit={handleSubmit}
            onCancel={() => setShowForm(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Lead Detail Sheet — ortak bileşen (admin + agent) */}
      <ContactDetailSheet
        lead={selectedLead}
        open={!!selectedLead}
        onOpenChange={(o) => !o && setSelectedLead(null)}
        onEdit={(l) => { setSelectedLead(null); setEditingLead(l); setShowForm(true); }}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kontakt löschen?</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Möchten Sie "{deleteConfirm?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate(deleteConfirm.id)}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
